// web/src/pages/MyCollection.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { auth, db, storage } from "@/lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection as fsCollection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  getDocs,
  limit,
  startAfter,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
  type Timestamp,
} from "firebase/firestore";
import { ref as storageRef, deleteObject } from "firebase/storage";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import ScanOCRModal from "@/features/scan/ScanOCRModal";
import AddToCollectionModal from "@/components/AddToCollectionModal";
import EditCollectionModal from "@/components/EditCollectionModal";
import DeckShelf from "@/components/decks/DeckShelf";
import BinderShelf from "@/components/binders/BinderShelf";
import { CardTile, FoilStyles } from "@/components/cards/CardTile";
import WishlistVariantModal from "@/components/wishlist/WishlistVariantModal";
import { fetchScryfallCardFullById } from "@/components/scan/CardScanner";
import {
  listenAssignedCardIds,
  listenBinders,
  addCardToBinder,
} from "@/data/binders";
import { getDropPayload, setDragData, allowDrop } from "@/dnd";
import ImportDeckModal from "@/components/import/ImportDeckModal";

/* =========================
   Types
========================= */

type Condition = "NM" | "LP" | "MP" | "HP" | "DMG";
type Status = "sale" | "trade" | "keep";

type UserCardDoc = {
  id: string;
  scryfall_id?: string | null;
  name: string;
  set?: string | null;
  number?: string | null;
  image?: string | null;
  foil?: boolean;
  condition?: Condition;
  qty?: number;
  status?: Status | null;
  for_sale?: boolean | null;
  for_trade?: boolean | null;
  price?: number | null;
  notes?: string | null;
  custom_image_url?: string | null;
  custom_image_path?: string | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  // Optional fields used for visual grouping/sorting
  type_line?: string | null;
  colors?: string[] | null;
  color_identity?: string[] | null;
  mana_value?: number | null;
  // full Scryfall JSON if present in doc
  scryfall_full?: any | null;
};

type WishDoc = {
  id: string;
  scryfall_id?: string | null;
  name: string;
  set?: string | null;
  number?: string | null;
  image?: string | null;
  createdAt?: Timestamp | null;
};

type BinderLite = { id: string; name: string; public?: boolean };
type DeckLite = {
  id: string;
  name: string;
  public?: boolean;
  coverCardId?: string | null;
  slug?: string;
  commanderId?: string | null;
  colorIdentity?: string[] | null;
  format?: string | null;
};

// Slug helper (deck URLs)
function deckSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "deck";
}

// === ADDED: visibility + cover + helper mutations ===
async function toggleBinderPublic(binder: BinderLite, uid?: string | null) {
  if (!uid) return;
  try {
    await updateDoc(doc(db, "users", uid, "binders", binder.id), {
      public: !binder.public,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("toggleBinderPublic failed", e);
  }
}
async function toggleDeckPublic(deck: DeckLite, uid?: string | null) {
  if (!uid) return;
  try {
    await updateDoc(doc(db, "users", uid, "decks", deck.id), {
      public: !deck.public,
      slug: deck.slug || deckSlug(deck.name),
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("toggleDeckPublic failed", e);
  }
}
async function setDeckCoverCard(deck: DeckLite, uid?: string | null) {
  if (!uid) return;
  const cardId = window.prompt(
    "Enter a card ID from this deck to use as cover (leave blank to clear):",
    deck.coverCardId || "",
  );
  if (cardId === null) return;
  try {
    if (cardId.trim()) {
      await updateDoc(doc(db, "users", uid, "decks", deck.id), {
        coverCardId: cardId.trim(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "users", uid, "decks", deck.id), {
        coverCardId: null,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.warn("setDeckCoverCard failed", e);
  }
}

/* =========================
   Paging
========================= */

const offersCountCache: Map<string, number> = new Map();

const REMOTE_PAGE_SIZE = 30; // Firestore fetch size
const LOCAL_PAGE_SIZE_3x3 = 9; // 3×3 tiles per view page

/* =========================
   Helpers
========================= */

function useMountedRef() {
  const m = useRef(true);
  useEffect(() => {
    return () => {
      m.current = false;
    };
  }, []);
  return m;
}

// --- MAPPING HELPERS (ADD) ---
function mapCardDoc(d: QueryDocumentSnapshot<DocumentData>): UserCardDoc {
  const v = d.data() as any;
  const full = v.scryfall_full || null;

  const fullImg =
    full?.image_uris?.normal ??
    full?.image_uris?.large ??
    (Array.isArray(full?.card_faces)
      ? (full.card_faces[0]?.image_uris?.normal ??
         full.card_faces[0]?.image_uris?.large ??
         null)
      : null) ??
    null;

  const derivedType = v.type_line ?? full?.type_line ?? null;
  const derivedColors = Array.isArray(v.colors)
    ? v.colors
    : (Array.isArray(full?.colors) ? full.colors : null);
  const derivedCI = Array.isArray(v.color_identity)
    ? v.color_identity
    : (Array.isArray(full?.color_identity) ? full.color_identity : null);
  const derivedMV =
    typeof v.mana_value === "number"
      ? v.mana_value
      : typeof v.cmc === "number"
      ? v.cmc
      : typeof full?.mana_value === "number"
      ? full.mana_value
      : typeof full?.cmc === "number"
      ? full.cmc
      : null;

  return {
    id: d.id,
    scryfall_id: v.scryfall_id ?? null,
    name: v.name ?? "Unknown",
    set: v.set ?? null,
    number: v.number ?? null,
    image: v.image ?? fullImg ?? null,
    foil: !!v.foil,
    condition: (v.condition as Condition) ?? "NM",
    qty: v.qty ?? 1,
    status: (v.status as Status) ?? null,
    for_sale: typeof v.for_sale === "boolean" ? v.for_sale : null,
    for_trade: typeof v.for_trade === "boolean" ? v.for_trade : null,
    price: typeof v.price === "number" ? v.price : null,
    notes: v.notes ?? "",
    custom_image_url: v.custom_image_url ?? null,
    custom_image_path: v.custom_image_path ?? null,
    createdAt: v.createdAt ?? null,
    updatedAt: v.updatedAt ?? null,
    type_line: derivedType,
    colors: derivedColors,
    color_identity: derivedCI,
    mana_value: derivedMV,
    scryfall_full: full,
  };
}

function mapWishDoc(d: QueryDocumentSnapshot<DocumentData>): WishDoc {
  const v = d.data() as any;
  return {
    id: d.id,
    scryfall_id: v.scryfall_id ?? null,
    name: v.name ?? "Unknown",
    set: v.set ?? null,
    number: v.number ?? null,
    image: v.image ?? null,
    createdAt: v.createdAt ?? null,
  };
}

// --- ADD (Storage + purge helpers)
async function deleteFromStorageSafe(raw: string) {
  if (!raw) return;
  try {
    if (!/^https?:\/\//i.test(raw)) {
      const r = storageRef(storage, raw);
      await deleteObject(r);
      return;
    }
    const m = raw.match(/^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?]+)(?:\?|$)/i);
    if (m) {
      const objectPath = decodeURIComponent(m[2]);
      const appBucket = (storage as any)?._bucket?.name || (storage as any)?._location?.bucket;
      if (!appBucket || appBucket === m[1]) {
        const r = storageRef(storage, objectPath);
        await deleteObject(r);
      } else {
        console.warn("[MyCollection] skip delete: bucket mismatch", m[1], objectPath);
      }
      return;
    }
    const gs = raw.match(/^gs:\/\/([^/]+)\/(.+)$/i);
    if (gs) {
      const appBucket = (storage as any)?._bucket?.name || (storage as any)?._location?.bucket;
      if (!appBucket || appBucket === gs[1]) {
        const r = storageRef(storage, gs[2]);
        await deleteObject(r);
      } else {
        console.warn("[MyCollection] skip delete (gs bucket mismatch)", gs[1], gs[2]);
      }
      return;
    }
    console.warn("[MyCollection] storage delete skipped (non-storage URL)", raw);
  } catch (err) {
    console.warn("[MyCollection] storage delete skipped:", raw, err);
  }
}

async function purgeAllCardRefs(uid: string, cardId: string) {
  try {
    const pubAssigns = await getDocs(
      fsCollection(db, "users", uid, "public_binder_assignments")
    );
    const delOps: Promise<any>[] = [];
    pubAssigns.forEach((d) => {
      const v = d.data() as any;
      if (v?.userCardId === cardId) delOps.push(deleteDoc(d.ref));
    });
    await Promise.allSettled(delOps);
  } catch (e) {
    console.warn("[MyCollection] purge public binder assignments failed", e);
  }

  try {
    const bindersCol = fsCollection(db, "users", uid, "binders");
    const bindersSnap = await getDocs(bindersCol);
    const binderOps: Promise<any>[] = [];
    bindersSnap.forEach((b) => {
      binderOps.push(
        (async () => {
          const slotsSnap = await getDocs(
            fsCollection(db, "users", uid, "binders", b.id, "slots")
          );
            const slotDel: Promise<any>[] = [];
            slotsSnap.forEach((s) => {
              const v = s.data() as any;
              if (String(v?.userCardId) === cardId) slotDel.push(deleteDoc(s.ref));
            });
            await Promise.allSettled(slotDel);
        })()
      );
    });
    await Promise.allSettled(binderOps);
  } catch (e) {
    console.warn("[MyCollection] purge binder slots failed", e);
  }

  try {
    const pubDeckSlots = await getDocs(
      fsCollection(db, "users", uid, "public_deck_slots")
    );
    const delPub: Promise<any>[] = [];
    pubDeckSlots.forEach((d) => {
      const v = d.data() as any;
      if (v?.userCardId === cardId) delPub.push(deleteDoc(d.ref));
    });
    await Promise.allSettled(delPub);
  } catch (e) {
    console.warn("[MyCollection] purge public deck slots failed", e);
  }

  try {
    const decksCol = fsCollection(db, "users", uid, "decks");
    const decksSnap = await getDocs(decksCol);
    const deckOps: Promise<any>[] = [];
    decksSnap.forEach((deck) => {
      deckOps.push(
        (async () => {
          const slotsSnap = await getDocs(
            fsCollection(db, "users", uid, "decks", deck.id, "slots")
          );
          const slotDel: Promise<any>[] = [];
          slotsSnap.forEach((s) => {
            const v = s.data() as any;
            if (String(v?.userCardId) === cardId) slotDel.push(deleteDoc(s.ref));
          });
          await Promise.allSettled(slotDel);
        })()
      );
    });
    await Promise.allSettled(deckOps);
  } catch (e) {
    console.warn("[MyCollection] purge deck slots failed", e);
  }
}

/* =========================
   Component
========================= */

export default function MyCollection() {
  const mountedRef = useMountedRef();
  const [tab, setTab] = useState<"collection" | "wishlist">("collection");

  // Quick-search integration: wishlist search term (collection uses `search`)
  const [wishSearch, setWishSearch] = useState("");
  // Collapsible shelves
  const [showBinders, setShowBinders] = useState(false);
  const [showDecks, setShowDecks] = useState(false);

  // external quick-search sync (kept)
  const externalFilterRef = useRef<string>("");
  const [externalActive, setExternalActive] = useState(false);

  // Auth
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [ready, setReady] = useState<boolean>(!!auth.currentUser);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!mountedRef.current) return;
      setUid(u?.uid ?? null);
      setReady(true);
    });
    return () => unsub();
  }, [mountedRef]);

  /* ----- Binders (assigned IDs) ----- */
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!uid) return;
    return listenAssignedCardIds(uid, (ids) => {
      if (mountedRef.current) setAssignedIds(ids);
    });
  }, [uid, mountedRef]);

  // Binder slots map (authoritative)
  const binderSlotMapRef = useRef<Map<string, Set<string>>>(new Map());
  const [binderSlotsVersion, setBinderSlotsVersion] = useState(0);

  /* ----- Binder list ----- */
  const [binders, setBinders] = useState<BinderLite[]>([]);
  const [bindersErr, setBindersErr] = useState<string | null>(null);
  useEffect(() => {
    if (!uid) return;
    return listenBinders(
      (list) => {
        if (!mountedRef.current) return;
        setBinders(list.map((b: any) => ({
  id: b.id,
  name: b.name || "Binder",
  public: !!b.public,
})));
        setBindersErr(null);
      },
      uid,
      (e) => {
        if (!mountedRef.current) return;
        setBindersErr(e?.message || "Failed to load binders");
      },
    );
  }, [uid, mountedRef]);

  // Subscribe to binder slots after binders arrive
  useEffect(() => {
    if (!uid) return;
    if (!binders.length) {
      binderSlotMapRef.current.clear();
      setBinderSlotsVersion((v) => v + 1);
      return;
    }
    const unsubs = binders.map((b) => {
      const col = fsCollection(db, "users", uid, "binders", b.id, "slots");
      return onSnapshot(col, (snap) => {
        const ids = new Set<string>();
        snap.forEach((sd) => {
          const v = sd.data() as any;
          const raw =
            v?.userCardId ??
            v?.cardId ??
            v?.user_card_id ??
            null;
          const id = raw ? String(raw) : null;
          if (id) ids.add(id);
        });
        binderSlotMapRef.current.set(b.id, ids);
        setBinderSlotsVersion((v) => v + 1);
      });
    });
    return () => {
      unsubs.forEach((u) => u());
      binderSlotMapRef.current.clear();
      setBinderSlotsVersion((v) => v + 1);
    };
  }, [uid, binders]);

  /* ----- Deck list (for 'Add to deck' picker) ----- */
  const [decks, setDecks] = useState<DeckLite[]>([]);
  const [decksErr, setDecksErr] = useState<string | null>(null);
  useEffect(() => {
    if (!uid) return;
    const col = fsCollection(db, "users", uid, "decks");
    const qy = query(col, orderBy("position", "asc"), orderBy("__name__", "asc"));
    return onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            name: (v.name || "Deck") as string,
            public: !!v.public,
            coverCardId: v.coverCardId || null,
            slug: v.slug || deckSlug(v.name || d.id),
            commanderId:
              v.commanderUserCardId ||
              v.commander_card_user_id ||
              v.commanderCardUserId ||
              v.commanderCardId ||
              v.commander_card_id ||
              v.primaryCommanderId ||
              v.commanderId ||
              null,
            colorIdentity:
              v.colorIdentity ||
              v.color_identity ||
              v.colors ||
              v.identity ||
              v.color_identity_codes ||
              null,
              format: v.format || v.deckFormat || v.type || null,
          } as DeckLite;
        });
        if (mountedRef.current) {
          setDecks(rows);
          setDecksErr(null);
        }
      },
      (e) => mountedRef.current && setDecksErr(e?.message || "Failed to load decks"),
    );
  }, [uid, mountedRef]);

  // Track all deck slots (which cards are assigned anywhere)
  const [deckAssignedIds, setDeckAssignedIds] = useState<Set<string>>(new Set());
  const deckSlotMapRef = useRef<Map<string, Set<string>>>(new Map());
  const [deckSlotsVersion, setDeckSlotsVersion] = useState(0);

  useEffect(() => {
    if (!uid) return;
    if (!decks.length) {
      deckSlotMapRef.current.clear();
      setDeckAssignedIds(new Set());
      setDeckSlotsVersion((v) => v + 1);
      return;
    }
    const unsubs = decks.map((d) => {
      const col = fsCollection(db, "users", uid, "decks", d.id, "slots");
      return onSnapshot(col, (snap) => {
        const ids = new Set<string>();
        snap.forEach((sd) => {
          const v = sd.data() as any;
          const raw =
            v?.userCardId ??
            v?.cardId ??
            v?.user_card_id ??
            null;
          const id = raw ? String(raw) : null;
          if (id) ids.add(id);
        });
        deckSlotMapRef.current.set(d.id, ids);
        const union = new Set<string>();
        for (const set of deckSlotMapRef.current.values()) for (const x of set) union.add(x);
        setDeckAssignedIds(union);
        setDeckSlotsVersion((v) => v + 1);
      });
    });
    return () => {
      unsubs.forEach((u) => u());
      deckSlotMapRef.current.clear();
      setDeckAssignedIds(new Set());
      setDeckSlotsVersion((v) => v + 1);
    };
  }, [uid, decks]);

  /* ----- Wishlist edit modal state ----- */
  const [wishlistEditOpen, setWishlistEditOpen] = useState(false);
  const [wishlistEditItem, setWishlistEditItem] = useState<WishDoc | null>(null);

  /* ----- Filters & UI state ----- */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<"all" | "sale" | "trade" | "keep">("all");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  type GroupBy = "none" | "type" | "color" | "set";
  type SortBy = "price" | "name" | "added" | "mana" | "type" | "set" | "color";
  type SortDir = "asc" | "desc";
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [sortBy, setSortBy] = useState<SortBy>("added");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [sortWithinGroups, setSortWithinGroups] = useState<boolean>(true);

  const [paged3x3, setPaged3x3] = useState<boolean>(false);

  // Hide cards assigned to binders/decks
  // Default FALSE so user initially sees full collection (including binder/deck assigned)
  const [hideBinderCards, setHideBinderCards] = useState<boolean>(false);

  const [colPage, setColPage] = useState<number>(0);
  const [wishPage, setWishPage] = useState<number>(0);

  const tabBtnClass = useCallback(
    (target: "collection" | "wishlist") => {
      const base =
        "px-3 py-1.5 rounded text-sm font-medium border transition focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-white/30";
      const active = "bg-white/15 border-white/40 text-white";
      const idle =
        "bg-black/40 border-white/15 text-white/70 hover:bg-white/10";
      return `${base} ${tab === target ? active : idle}`;
    },
    [tab]
  );

  // Touch swipe helpers (mobile)
  const touchStartXRef = useRef<number | null>(null);
  const SWIPE_MIN_PX = 40;
  const onTouchStart = (e: React.TouchEvent, _which: "collection" | "wishlist") => {
    touchStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (
    e: React.TouchEvent,
    which: "collection" | "wishlist",
    totalPages: number,
  ) => {
    const sx = touchStartXRef.current;
    touchStartXRef.current = null;
    if (sx == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? sx) - sx;
    if (Math.abs(dx) < SWIPE_MIN_PX) return;
    if (which === "collection") {
      setColPage((p) => (dx < 0 ? Math.min(p + 1, Math.max(0, totalPages - 1)) : Math.max(0, p - 1)));
    } else {
      setWishPage((p) => (dx < 0 ? Math.min(p + 1, Math.max(0, totalPages - 1)) : Math.max(0, p - 1)));
    }
  };

  /* =========================
     Helpers for grouping/sorting
  ========================= */

  function getTypeGroup(c: UserCardDoc): string {
    const tl = (c.type_line || "").toLowerCase();
    if (!tl) return "Unknown";
    if (tl.includes("creature")) return "Creature";
    if (tl.includes("instant")) return "Instant";
    if (tl.includes("sorcery")) return "Sorcery";
    if (tl.includes("enchantment")) return "Enchantment";
    if (tl.includes("artifact")) return "Artifact";
    if (tl.includes("planeswalker")) return "Planeswalker";
    if (tl.includes("battle")) return "Battle";
    if (tl.includes("land")) return "Land";
    return "Other";
  }
  function getColorGroup(c: UserCardDoc): string {
    const ids =
      (Array.isArray(c.color_identity) ? c.color_identity :
        Array.isArray(c.colors) ? c.colors : null) || [];
    const set = new Set(ids.map((x) => String(x).toUpperCase()));
    const order = ["W", "U", "B", "R", "G"] as const;
    const present = order.filter((x) => set.has(x));
    if (present.length === 0) return "Colorless";
    if (present.length === 1) {
      const map: Record<string, string> = { W: "White", U: "Blue", B: "Black", R: "Red", G: "Green" };
      return map[present[0]] || "Colorless";
    }
    return "Multicolor";
  }
  function getSetGroup(c: UserCardDoc): string {
    return (c.set || "Unknown").toUpperCase();
  }

  function tsToMillis(t?: Timestamp | null): number {
    if (!t || typeof (t as any)?.toMillis !== "function") return Number.NEGATIVE_INFINITY;
    try {
      return t.toMillis();
    } catch {
      return Number.NEGATIVE_INFINITY;
    }
  }

  function getSortKey(c: UserCardDoc, by: SortBy): string | number {
    switch (by) {
      case "price": {
        const p = typeof c.price === "number" && Number.isFinite(c.price) ? c.price : Number.POSITIVE_INFINITY;
        return p;
      }
      case "name":
        return (c.name || "").toLowerCase();
      case "added": {
        const ms = tsToMillis(c.createdAt);
        return ms === Number.NEGATIVE_INFINITY ? Number.POSITIVE_INFINITY : ms;
      }
      case "mana": {
        const mv = typeof c.mana_value === "number" ? c.mana_value : Number.POSITIVE_INFINITY;
        return mv;
      }
      case "type":
        return getTypeGroup(c);
      case "set":
        return getSetGroup(c);
      case "color":
        return getColorGroup(c);
      default:
        return 0;
    }
  }

  function compareCards(a: UserCardDoc, b: UserCardDoc, by: SortBy, dir: SortDir): number {
    const va = getSortKey(a, by);
    const vb = getSortKey(b, by);

    if (typeof va === "number" && typeof vb === "number") {
      const aMissing = !Number.isFinite(va);
      const bMissing = !Number.isFinite(vb);
      if (aMissing && !bMissing) return 1;
      if (!aMissing && bMissing) return -1;
      const diff = va - vb;
      const cmp = Number.isFinite(diff) ? diff : 0;
      return dir === "asc" ? cmp : -cmp;
    }

    const sa = String(va ?? "");
    const sb = String(vb ?? "");
    const cmp = sa.localeCompare(sb, undefined, { sensitivity: "base", numeric: true });
    return dir === "asc" ? cmp : -cmp;
  }

  const colorOrder = ["White", "Blue", "Black", "Red", "Green", "Multicolor", "Colorless", "Unknown"];
  const typeOrder = ["Creature", "Instant", "Sorcery", "Enchantment", "Artifact", "Planeswalker", "Battle", "Land", "Other", "Unknown"];

  function orderGroupKeys(keys: string[]): string[] {
    if (groupBy === "color") {
      return [...keys].sort((a, b) => colorOrder.indexOf(a) - colorOrder.indexOf(b));
    }
    if (groupBy === "type") {
      return [...keys].sort((a, b) => typeOrder.indexOf(a) - typeOrder.indexOf(b));
    }
    return [...keys].sort((a, b) => a.localeCompare(b));
  }

  /* =========================
   Card loads (paged)
  ========================= */

  const [cards, setCards] = useState<UserCardDoc[]>([]);
  const [allCards, setAllCards] = useState<UserCardDoc[] | null>(null);
  const [allCardsLoading, setAllCardsLoading] = useState(false);

  const lastCardRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const loadingCardsRef = useRef(false);
  const [pendingCards, setPendingCards] = useState(false);
  const [hasMoreCards, setHasMoreCards] = useState(false);

  /* ----- Wishlist (paged) ----- */
  const [wishes, setWishes] = useState<WishDoc[]>([]);
  const lastWishRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreWishes, setHasMoreWishes] = useState(false);
  const [pendingWishes, setPendingWishes] = useState(false);
  const loadingWishesRef = useRef(false);

  // Scryfall metadata cache (presentation-only)
  const scryMetaCacheRef = useRef<
    Map<string, { type_line: string | null; color_identity: string[] | null; colors: string[] | null }>
  >(new Map());
  const scryMetaPendingRef = useRef<Set<string>>(new Set());
  const backfillPendingRef = useRef<Set<string>>(new Set());

  /* ----- Import / Scan ----- */
  const [importOpen, setImportOpen] = useState(false);
  const [importDest, setImportDest] = useState<"collection" | "wishlist">("collection");
  const [scanOpen, setScanOpen] = useState(false);

  /* ----- Add / Edit modals ----- */
  const [addOpen, setAddOpen] = useState(false);
  const [addCard, setAddCard] = useState<any | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editCard, setEditCard] = useState<any | null>(null);
  const [editExisting, setEditExisting] = useState<{
    docId: string;
    values: {
      qty: number;
      condition: Condition;
      foil: boolean;
      for_sale: boolean;
      for_trade: boolean;
      price: number | null;
      notes: string;
      custom_image_url: string | null;
      status?: Status;
    };
  } | null>(null);

  const openEditor = useCallback((c: UserCardDoc) => {
    setEditCard(c);
    setEditExisting({
      docId: c.id,
      values: {
        qty: c.qty ?? 1,
        condition: (c.condition as Condition) ?? "NM",
        foil: !!c.foil,
        for_sale: !!c.for_sale,
        for_trade: !!c.for_trade,
        price: c.price ?? null,
        notes: c.notes ?? "",
        custom_image_url: c.custom_image_url ?? null,
        status: c.status,
      },
    });
    setEditOpen(true);
  }, []);

  /* ----- Move to Binder ----- */
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveCardId, setMoveCardId] = useState<string | null>(null);
  const openMoveDialog = (cardId: string) => {
    setMoveCardId(cardId);
    setMoveOpen(true);
  };
  const handleSelectBinder = useCallback(
    async (binderId: string) => {
      if (!uid || !moveCardId) return;
      try {
        await addCardToBinder(binderId, moveCardId, uid);
        if (!mountedRef.current) return;
        setMoveOpen(false);
        setMoveCardId(null);
      } catch (e: any) {
        if (mountedRef.current) setErrorMsg(e?.message || "Failed to move card.");
      }
    },
    [uid, moveCardId, mountedRef],
  );

  /* ----- Move to Deck ----- */
  const [moveDeckOpen, setMoveDeckOpen] = useState(false);
  const [moveDeckCardId, setMoveDeckCardId] = useState<string | null>(null);
  const openMoveToDeck = (cardId: string) => {
    setMoveDeckCardId(cardId);
    setMoveDeckOpen(true);
  };
  const handleSelectDeck = useCallback(
    async (deckId: string) => {
      if (!uid || !moveDeckCardId) return;
      try {
        await addDoc(fsCollection(db, "users", uid, "decks", deckId, "slots"), {
          userCardId: moveDeckCardId,
          createdAt: serverTimestamp(),
        });
        if (!mountedRef.current) return;
        setMoveDeckOpen(false);
        setMoveDeckCardId(null);
      } catch (e: any) {
        if (mountedRef.current) setErrorMsg(e?.message || "Failed to add card to deck.");
      }
    },
    [uid, moveDeckCardId, mountedRef],
  );

  useEffect(() => {
    (window as any).__openMoveToDeck = openMoveToDeck;
    return () => {
      if ((window as any).__openMoveToDeck === openMoveToDeck) {
        delete (window as any).__openMoveToDeck;
      }
    };
  }, [openMoveToDeck]);

  /* ----- Oracle modal ----- */
  const [oracleOpen, setOracleOpen] = useState(false);
  const [oracleBusy, setOracleBusy] = useState(false);
  const oracleAcRef = useRef<AbortController | null>(null);
  const [oracleSource, setOracleSource] = useState<UserCardDoc | WishDoc | null>(null);
  const [oracleFrom, setOracleFrom] = useState<"collection" | "wishlist" | null>(null);
  const [oracleCard, setOracleCard] = useState<{
    name: string;
    scryfall_id?: string | null;
    image?: string | null;
    set?: string | null;
    number?: string | null;
    oracle_text?: string | null;
  } | null>(null);

  const openOracle = useCallback(
    (from: "collection" | "wishlist", c: UserCardDoc | WishDoc) => {
      setOracleFrom(from);
      setOracleSource(c);
      setOracleCard({
        name: c.name,
        scryfall_id: c.scryfall_id ?? null,
        image: c.image ?? null,
        set: (c as any).set ?? null,
        number: (c as any).number ?? null,
        oracle_text: (c as any).oracle_text ?? null,
      });
      setOracleOpen(true);
    },
    []
  );

  const closeOracle = useCallback(() => {
    if (oracleAcRef.current) oracleAcRef.current.abort();
    setOracleOpen(false);
    setOracleSource(null);
    setOracleCard(null);
    setOracleFrom(null);
    setOracleBusy(false);
  }, []);

  useEffect(() => {
    if (!oracleOpen || !oracleSource) return;
    const scryId = oracleSource.scryfall_id;
    if (!scryId) return;
    if (oracleCard?.oracle_text && oracleCard.scryfall_id === scryId) return;

    const ac = new AbortController();
    oracleAcRef.current = ac;
    setOracleBusy(true);

    (async () => {
      try {
        const sf = await fetchScryfallCardFullById(scryId);
        if (ac.signal.aborted || !sf) return;
        setOracleCard((prev) => {
          const img =
            prev?.image ||
            sf?.image_uris?.normal ||
            sf?.image_uris?.large ||
            (Array.isArray(sf?.card_faces)
              ? (sf.card_faces[0]?.image_uris?.normal ||
                 sf.card_faces[0]?.image_uris?.large ||
                 null)
              : null) ||
            null;
          return {
            name: sf.name ?? prev?.name ?? "Unknown",
            scryfall_id: scryId,
            image: img,
            set: sf.set ?? prev?.set ?? null,
            number: sf.collector_number ?? prev?.number ?? null,
            oracle_text: sf.oracle_text ?? prev?.oracle_text ?? null,
          };
        });
      } catch {
        /* ignore */
      } finally {
        if (!ac.signal.aborted) setOracleBusy(false);
      }
    })();

    return () => ac.abort();
  }, [oracleOpen, oracleSource, oracleCard]);

  /* =========================
     Initial reload on uid change
  ========================= */
  useEffect(() => {
    if (!ready || !uid) return;
    setCards([]);
    lastCardRef.current = null;
    setAllCards(null);
    setWishes([]);
    lastWishRef.current = null;
    setErrorMsg(null);
    setColPage(0);
    setWishPage(0);
    void Promise.all([loadCards(true, true), loadWishes(true)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, uid]);

  /* =========================
     Data loads (guard against overlap)
  ========================= */
  const loadCards = useCallback(
    async (initial = false, fetchAll = false) => {
      if (!uid) return;
      if (loadingCardsRef.current) return;
      loadingCardsRef.current = true;
      setPendingCards(true);
      try {
        const root = fsCollection(db, "users", uid, "cards");
        const baseOrder = [orderBy("createdAt", "desc"), orderBy("__name__", "desc")];

        let localLast: QueryDocumentSnapshot<DocumentData> | null =
          initial ? null : lastCardRef.current;

        const aggregate: UserCardDoc[] = initial ? [] : [...cards];
        let keepLooping = true;
        let safety = 0;

        while (keepLooping) {
          const qy = localLast
            ? query(root, ...baseOrder, startAfter(localLast), limit(REMOTE_PAGE_SIZE))
            : query(root, ...baseOrder, limit(REMOTE_PAGE_SIZE));

            const snap = await getDocs(qy);
            const docs = snap.docs as QueryDocumentSnapshot<DocumentData>[];
            if (docs.length > 0) localLast = docs[docs.length - 1];

            const mapped: UserCardDoc[] = docs.map(mapCardDoc);
            aggregate.push(...mapped);

            // stop conditions
            if (docs.length < REMOTE_PAGE_SIZE) keepLooping = false;
            if (!fetchAll) keepLooping = false;
            if (safety++ > 500) keepLooping = false; // absolute safety
        }

        if (!mountedRef.current) return;
        lastCardRef.current = localLast;
        setCards(aggregate);
        setHasMoreCards(false); // all loaded
      } catch (e: any) {
        console.warn("[MyCollection] loadCards (full) failed:", e);
        if (mountedRef.current) setErrorMsg(e?.message ?? "Couldn't load cards.");
      } finally {
        if (mountedRef.current) setPendingCards(false);
        loadingCardsRef.current = false;
      }
    },
    [uid, mountedRef, cards],
  );

  const loadWishes = useCallback(
    async (initial = false) => {
      if (!uid) return;
      if (loadingWishesRef.current) return;
      loadingWishesRef.current = true;
      setPendingWishes(true);
      try {
        const root = fsCollection(db, "users", uid, "wishlist");
        const qy =
          initial || !lastWishRef.current
            ? query(root, orderBy("createdAt", "desc"), limit(REMOTE_PAGE_SIZE))
            : query(root, orderBy("createdAt", "desc"), startAfter(lastWishRef.current), limit(REMOTE_PAGE_SIZE));

        const snap = await getDocs(qy);
        const docs = snap.docs as QueryDocumentSnapshot<DocumentData>[];
        if (docs.length > 0) lastWishRef.current = docs[docs.length - 1];

        const mapped: WishDoc[] = docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            scryfall_id: v.scryfall_id ?? null,
            name: v.name ?? "Unknown",
            set: v.set ?? null,
            number: v.number ?? null,
            image: v.image ?? null,
            createdAt: v.createdAt ?? null,
          };
        });

        if (!mountedRef.current) return;
        setWishes((prev) => (initial ? mapped : prev.concat(mapped)));
        setHasMoreWishes(docs.length === REMOTE_PAGE_SIZE);
      } catch (e: any) {
        console.warn("[MyCollection] loadWishes failed:", e);
        if (mountedRef.current) setErrorMsg(e?.message ?? "Couldn't load wishlist.");
      } finally {
        if (mountedRef.current) setPendingWishes(false);
        loadingWishesRef.current = false;
      }
    },
    [uid, mountedRef],
  );

  /* =========================
     Realtime listeners (ADD)
  ========================= */
  useEffect(() => {
    if (!uid) return;
    const root = fsCollection(db, "users", uid, "cards");
    const qy = query(
      root,
      orderBy("createdAt", "desc"),
      orderBy("__name__", "desc"),
      limit(REMOTE_PAGE_SIZE)
    );
    const unsub = onSnapshot(qy, (snap) => {
      const changes = snap.docChanges();
      if (!changes.length) return;

      setCards((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        for (const ch of changes) {
          const id = ch.doc.id;
          if (ch.type === "removed") {
            map.delete(id);
          } else {
            map.set(id, mapCardDoc(ch.doc));
          }
        }
        const arr = Array.from(map.values());
        arr.sort((a, b) => compareCards(a, b, sortBy, sortDir));
        return arr;
      });

      setAllCards((prev) => {
        if (!prev) return prev;
        const map = new Map(prev.map((c) => [c.id, c]));
        for (const ch of changes) {
          const id = ch.doc.id;
          if (ch.type === "removed") {
            map.delete(id);
          } else {
            map.set(id, mapCardDoc(ch.doc));
          }
        }
        const arr = Array.from(map.values());
        arr.sort((a, b) => compareCards(a, b, sortBy, sortDir));
        return arr;
      });
    });
    return () => unsub();
  }, [uid, sortBy, sortDir]);

  useEffect(() => {
    if (!uid) return;
    const root = fsCollection(db, "users", uid, "wishlist");
    const qy = query(
      root,
      orderBy("createdAt", "desc"),
      orderBy("__name__", "desc"),
      limit(REMOTE_PAGE_SIZE)
    );
    const unsub = onSnapshot(qy, (snap) => {
      const changes = snap.docChanges();
      if (!changes.length) return;
      setWishes((prev) => {
        const map = new Map(prev.map((w) => [w.id, w]));
        for (const ch of changes) {
          const id = ch.doc.id;
          if (ch.type === "removed") map.delete(id);
          else map.set(id, mapWishDoc(ch.doc));
        }
        const arr = Array.from(map.values());
        arr.sort((a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt));
        return arr;
      });
    });
    return () => unsub();
  }, [uid]);

  /* =========================
     Deletes (hard + retries)
  ========================= */

  function looksLikeGhost(c: UserCardDoc): boolean {
    const noMeta =
      !c.type_line &&
      (!Array.isArray(c.colors) || c.colors.length === 0) &&
      (!Array.isArray(c.color_identity) || c.color_identity.length === 0) &&
      c.mana_value == null &&
      !(c as any).scryfall_full;

    const noFlags =
      !c.for_sale &&
      !c.for_trade &&
      !c.status &&
      !c.price &&
      !c.custom_image_url;

    const weakName = !c.name || c.name === "Unknown";
    const missingTimestamps = !c.createdAt;

    return (noMeta || weakName || missingTimestamps) && noFlags && (c.qty ?? 1) <= 1;
  }

  async function purgeGhostMirrors(uid: string, cardId: string) {
    const tryDel = async (path: string[]) => {
      try {
        await deleteDoc(doc(db, ...path));
      } catch {
        /* ignore */
      }
    };
    await Promise.allSettled([
      tryDel(["users", uid, "public_cards", cardId]),
      tryDel(["users", uid, "public_inventory", cardId]),
      tryDel(["users", uid, "public_sale", cardId]),
      tryDel(["users", uid, "public_trade", cardId]),
      tryDel(["users", uid, "search_cards", cardId]),
    ]);
  }

  async function purgeGhostDuplicatesByScryfallId(uid: string, base: UserCardDoc) {
    if (!base.scryfall_id) return;
    try {
      const qy = query(
        fsCollection(db, "users", uid, "cards"),
        where("scryfall_id", "==", base.scryfall_id),
        limit(50)
      );
      const snap = await getDocs(qy);
      const doomed = snap.docs
        .map(mapCardDoc)
        .filter((x) => x.id !== base.id && looksLikeGhost(x));

      if (!doomed.length) return;

      await Promise.allSettled(
        doomed.map((g) => purgeAllCardRefs(uid, g.id))
      );
      await Promise.allSettled(
        doomed.map((g) => deleteDoc(doc(db, "users", uid, "cards", g.id)))
      );

      setCards((cur) => cur.filter((x) => !doomed.some((g) => g.id === x.id)));
      setAllCards((cur) =>
        cur ? cur.filter((x) => !doomed.some((g) => g.id === x.id)) : cur
      );
    } catch {
      /* ignore */
    }
  }

  const onDeleteCard = useCallback(
    async (payload: UserCardDoc | string) => {
      if (!uid) return;
      const id = typeof payload === "string" ? payload : payload?.id;
      if (!id) return;

      const prevCards = cards;
      setCards((cur) => cur.filter((c) => c.id !== id));
      setAllCards((cur) => (cur ? cur.filter((c) => c.id !== id) : cur));

      try {
        await deleteDoc(doc(db, "users", uid, "cards", id));
      } catch (e: any) {
        console.warn("[MyCollection] delete failed:", e);
        setCards(prevCards);
        setAllCards((cur) => {
          if (!cur) return cur;
          const missing = prevCards.filter(
            (p) => !cur.some((c) => c.id === p.id)
          );
          return [...cur, ...missing];
        });
        setErrorMsg(
          "Couldn't delete card: " + (e?.message || "permission/network error")
        );
      }
    },
    [uid, cards]
  );

  const onDeleteWish = useCallback(
    async (id: string) => {
      if (!uid || !id) return;
      try {
        await deleteDoc(doc(db, "users", uid, "wishlist", id));
        if (mountedRef.current) setWishes((prev) => prev.filter((w) => w.id !== id));
      } catch (e) {
        console.warn("[MyCollection] delete wishlist failed:", e);
      }
    },
    [uid, mountedRef],
  );

  /* =========================
   Drag / Drop
========================= */

  const handleDropBackToCollection = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      if (!uid) return;
      const data = getDropPayload(e);
      if (
        !data?.userCardId ||
        !data?.sourceBinderId ||
        !data?.sourceSlotId
      ) return;
      try {
        await deleteDoc(
          doc(
            db,
            "users",
            uid,
            "binders",
            data.sourceBinderId,
            "slots",
            data.sourceSlotId,
          ),
        );
      } catch (err) {
        console.warn("[MyCollection] drop back to collection failed:", err);
      }
    },
    [uid],
  );

  /* =========================
     Background full fetch
  ========================= */
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        setAllCardsLoading(true);
        const root = fsCollection(db, "users", uid, "cards");
        const baseOrder = [orderBy("createdAt", "desc"), orderBy("__name__", "desc")];
        let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
        const collected: UserCardDoc[] = [];
        while (true) {
          const qy = cursor
            ? query(root, ...baseOrder, startAfter(cursor), limit(REMOTE_PAGE_SIZE))
            : query(root, ...baseOrder, limit(REMOTE_PAGE_SIZE));
          const snap = await getDocs(qy);
          const docs = snap.docs as QueryDocumentSnapshot<DocumentData>[];
          if (docs.length === 0) break;
          cursor = docs[docs.length - 1];

          const page: UserCardDoc[] = docs.map((d) => {
            const v = d.data() as any;
            const full = v.scryfall_full || null;
            const fullImg =
              full?.image_uris?.normal ??
              full?.image_uris?.large ??
              (Array.isArray(full?.card_faces)
                ? (full.card_faces[0]?.image_uris?.normal ??
                   full.card_faces[0]?.image_uris?.large ??
                   null)
                : null) ??
              null;
            const derivedType = v.type_line ?? full?.type_line ?? null;
            const derivedColors = Array.isArray(v.colors) ? v.colors : (Array.isArray(full?.colors) ? full.colors : null);
            const derivedCI = Array.isArray(v.color_identity) ? v.color_identity : (Array.isArray(full?.color_identity) ? full.color_identity : null);
            const derivedMV =
              typeof v.mana_value === "number"
                ? v.mana_value
                : typeof v.cmc === "number"
                ? v.cmc
                : typeof full?.mana_value === "number"
                ? full.mana_value
                : typeof full?.cmc === "number"
                ? full.cmc
                : null;

            return {
              id: d.id,
              scryfall_id: v.scryfall_id ?? null,
              name: v.name ?? "Unknown",
              set: v.set ?? null,
              number: v.number ?? null,
              image: v.image ?? fullImg ?? null,
              foil: !!v.foil,
              condition: (v.condition as Condition) ?? "NM",
              qty: v.qty ?? 1,
              status: (v.status as Status) ?? null,
              for_sale: typeof v.for_sale === "boolean" ? v.for_sale : null,
              for_trade: typeof v.for_trade === "boolean" ? v.for_trade : null,
              price: typeof v.price === "number" ? v.price : null,
              notes: v.notes ?? "",
              custom_image_url: v.custom_image_url ?? null,
              custom_image_path: v.custom_image_path ?? null,
              createdAt: v.createdAt ?? null,
              updatedAt: v.updatedAt ?? null,
              type_line: derivedType,
              colors: derivedColors,
              color_identity: derivedCI,
              mana_value: derivedMV,
              scryfall_full: full,
            } as UserCardDoc;
          });

          collected.push(...page);
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, 0));
        }
        if (!cancelled && mountedRef.current) setAllCards(collected);
      } catch {
        // ignore; UI falls back to paged list
      } finally {
        if (mountedRef.current) setAllCardsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, mountedRef]);

  /* =========================
     Filters + global sort/group
  ========================= */

  // union of binder-assigned (authoritative) + public mirror + deck-assigned ids
  const combinedAssigned = useMemo<Set<string>>(() => {
    const out = new Set<string>();

    // public mirror (nice to have)
    assignedIds.forEach((id) => out.add(id));

    // authoritative: private binder slots we listen to here
    for (const set of binderSlotMapRef.current.values()) {
      for (const id of set) out.add(id);
    }

    // decks
    deckAssignedIds.forEach((id) => out.add(id));
    return out;
  }, [assignedIds, deckAssignedIds, binderSlotsVersion]);

  // debug log for combinedAssigned
  useEffect(() => {
    try {
      const arr = Array.from(combinedAssigned || []);
      console.debug("[MyCollection] combinedAssigned count:", arr.length, arr.slice(0, 20));
    } catch (e) {
      console.debug("[MyCollection] combinedAssigned debug failed", e);
    }
  }, [combinedAssigned]);

  const baseAll = useMemo(() => (allCards ? allCards : cards), [allCards, cards]);

  const globallySorted = useMemo(() => {
    const arr = [...baseAll];
    arr.sort((a, b) => compareCards(a, b, sortBy, sortDir));
    return arr;
  }, [baseAll, sortBy, sortDir]);

  // Cards passed to DeckShelf
  const deckShelfCards = useMemo(() => globallySorted, [globallySorted]);

  /* ---------- Filter (pre-hide) so we can compute hidden count ---------- */
  const filteredBeforeHide = useMemo(() => {
    const qtxt = search.trim().toLowerCase();
    function matchesStatus(c: UserCardDoc) {
      if (statusFilter === "all") return true;
      const sale = !!c.for_sale || c.status === "sale";
      const trade = !!c.for_trade || c.status === "trade";
      const keep = !sale && !trade;
      if (statusFilter === "sale") return sale;
      if (statusFilter === "trade") return trade;
      if (statusFilter === "keep") return keep;
      return true;
    }
    return globallySorted.filter((c) => {
      if (!matchesStatus(c)) return false;
      if (!qtxt) return true;
      const hay = `${c.name} ${c.set ?? ""} ${c.number ?? ""}`.toLowerCase();
      return hay.includes(qtxt);
    });
  }, [globallySorted, search, statusFilter]);
  const displayCards = useMemo(
    () =>
      hideBinderCards
        ? filteredBeforeHide.filter((c) => !combinedAssigned.has(c.id))
        : filteredBeforeHide,
    [filteredBeforeHide, hideBinderCards, combinedAssigned]
  );
  const hiddenAssignedCount = useMemo(
    () => filteredBeforeHide.length - displayCards.length,
    [filteredBeforeHide.length, displayCards.length]
  );

  // Pagination + counts (5 rows * 6 cols)
  const ROWS_PER_PAGE = 5;
  const COLS_PER_ROW = 6;
  const COLLECTION_PAGE_SIZE = ROWS_PER_PAGE * COLS_PER_ROW;

  const [collectionPage, setCollectionPage] = useState(0);
  useEffect(() => {
    setCollectionPage(0);
  }, [search, statusFilter, hideBinderCards, groupBy]);

  const totalCollectionCount = useMemo(() => baseAll.length, [baseAll]);
  const filteredCount = useMemo(() => displayCards.length, [displayCards]);

  const totalPages = useMemo(
    () =>
      groupBy === "none"
        ? Math.max(1, Math.ceil(filteredCount / COLLECTION_PAGE_SIZE))
        : 1,
    [filteredCount, groupBy]
  );

  const pagedDisplayCards = useMemo(
    () =>
      groupBy === "none"
        ? displayCards.slice(
            collectionPage * COLLECTION_PAGE_SIZE,
            (collectionPage + 1) * COLLECTION_PAGE_SIZE
          )
        : displayCards,
    [displayCards, collectionPage, groupBy]
  );

  function gotoPage(n: number) {
    setCollectionPage((_) => Math.min(Math.max(0, n), totalPages - 1));
  }
  /* ========= END FIX ========= */

  /* ========= Deck full-collection picker state (ensure defined) ========= */
  const [deckCardPickerOpen, setDeckCardPickerOpen] = useState(false);
  const [deckCardPickerTarget, setDeckCardPickerTarget] = useState<string | null>(null);
  const [deckPickerSearch, setDeckPickerSearch] = useState("");

  function openDeckCardPicker(deckId: string) {
    setDeckCardPickerTarget(deckId);
    setDeckPickerSearch("");
    setDeckCardPickerOpen(true);
  }

  const fullCollectionForPicker = useMemo(() => {
    const source = allCards ?? cards;
    const q = deckPickerSearch.trim().toLowerCase();
    if (!q) return source;
    return source.filter((c) =>
      `${c.name} ${c.set ?? ""} ${c.number ?? ""}`.toLowerCase().includes(q)
    );
  }, [allCards, cards, deckPickerSearch]);

  async function addCardToDeck(deckId: string, cardId: string) {
    if (!uid) return;
    try {
      await addDoc(fsCollection(db, "users", uid, "decks", deckId, "slots"), {
        userCardId: cardId,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("addCardToDeck failed", e);
    }
  }
  /* ========= END deck picker additions ========= */

  /* =========================
     Helpers
  ========================= */
  const clearError = () => setErrorMsg(null);

  /* =========================
     Event handlers
  ========================= */
  const handleImportOpen = () => {
    clearError();
    setImportOpen(true);
  };

  const handleScanOpen = () => {
    clearError();
    setScanOpen(true);
  };

  // Listen for quick-search overlay filters and route term to collection or wishlist
  useEffect(() => {
    function onCollectionFilter(e: any) {
      const term = (e?.detail?.term ?? "") as string;
      if (tab === "collection") {
        externalFilterRef.current = term;
        setSearch(term);
        setExternalActive(!!term);
      } else {
        setWishSearch(term);
      }
    }
    function onWishlistFilter(e: any) {
      const term = (e?.detail?.term ?? "") as string;
      setWishSearch(term);
    }
    function onClose() {
      externalFilterRef.current = "";
      setSearch("");
      setWishSearch("");
      setExternalActive(false);
    }

    window.addEventListener("app:collectionFilter", onCollectionFilter as any);
    window.addEventListener("app:wishlistFilter", onWishlistFilter as any);
    window.addEventListener("app:closeQuickSearch", onClose as any);
    return () => {
      window.removeEventListener("app:collectionFilter", onCollectionFilter as any);
      window.removeEventListener("app:wishlistFilter", onWishlistFilter as any);
      window.removeEventListener("app:closeQuickSearch", onClose as any);
    };
  }, [tab]);

  /* =========================
     Render
  ========================= */
  return (
    <main className="mx-auto max-w-6xl p-4 sm:p-6 space-y-6 pb-[env(safe-area-inset-bottom)]">
      {/* Header */}
      <header className="space-y-4">
        {/* Row 1: Title + Import buttons */}
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-semibold text-white">My Collection</h1>
          <div className="flex items-center gap-2">
            <Button
              data-tour="collection-import"
              className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-blue-500 text-white border-0 hover:from-purple-500 hover:via-fuchsia-500 hover:to-cyan-400 focus:ring-2 focus:ring-purple-400/50 transition"
              onClick={handleImportOpen}
              title="Import"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="mr-2">
                <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 21h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Import
            </Button>
            <Button
              data-tour="collection-scan"
              className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-blue-500 text-white border-0 hover:from-purple-500 hover:via-fuchsia-500 hover:to-cyan-400 focus:ring-2 focus:ring-purple-400/50 transition"
              onClick={handleScanOpen}
              title="Scan"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="mr-2">
                <path d="M21 19V9a2 2 0 0 0-2-2h-2l-2-2H9L7 7H5a2 2 0 0 0-2 2v10h18Z" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="14" r="3.5" stroke="currentColor" strokeWidth="2" />
              </svg>
              Scan
            </Button>
          </div>
        </div>

        {/* Row 2: Search */}
        {/* NOTE: Search bar removed from top per request.
            Tabs moved into this header row (same row as Import/Scan buttons). */}

        {/* Tabs moved into header row with Import/Scan */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTab("collection")}
            className={`px-3 py-1.5 rounded text-sm font-medium border ${
              tab === "collection"
                ? "bg-indigo-600 text-white border-indigo-500"
                : "bg-black/40 text-slate-300 border-slate-600 hover:bg-black/50"
            }`}
          >
            Collection
          </button>
          <button
            type="button"
            onClick={() => setTab("wishlist")}
            className={`px-3 py-1.5 rounded text-sm font-medium border ${
              tab === "wishlist"
                ? "bg-indigo-600 text-white border-indigo-500"
                : "bg-black/40 text-slate-300 border-slate-600 hover:bg-black/50"
            }`}
          >
            Wishlist
          </button>
        </div>
      </header>

      {errorMsg && (
        <div className="rounded border border-red-700 bg-red-900/20 px-3 py-2 text-sm text-red-300">{errorMsg}</div>
      )}
      {bindersErr && (
        <div className="rounded border border-amber-700 bg-amber-900/20 px-3 py-2 text-sm text-amber-300">{bindersErr}</div>
      )}

      {/* Binder shelf (collapsible; default collapsed) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white">Binders</div>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 rounded border border-white/10 bg-black/40 text-sm text-gray-100"
              onClick={() => setShowBinders((s) => !s)}
              aria-expanded={showBinders}
            >
              {showBinders ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        {/* NEW: binder public toggles */}
        {binders.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {binders.map(b => (
              <div key={b.id} className="flex items-center gap-1 text-xs bg-black/40 border border-white/10 rounded px-2 py-1">
                <span className="text-slate-200 truncate max-w-[120px]">{b.name}</span>
                <button
                  onClick={() =>
                    toggleBinderPublicAndMirror(
                      b,
                      uid,
                      binderSlotMapRef.current.get(b.id) || new Set()
                    )
                  }
                  className={b.public
                    ? "px-1.5 py-0.5 rounded bg-emerald-600/80 text-white"
                    : "px-1.5 py-0.5 rounded bg-slate-600/60 text-white/80"}
                  title="Toggle public visibility"
                >
                  {b.public ? "Public" : "Private"}
                </button>
              </div>
            ))}
          </div>
        )}
        <div data-tour="collection-binders">
          {ready && uid && showBinders ? (
            <BinderShelf
              uid={uid}
              readOnly={false}
              onTogglePublic={(binderId, currentPublic) => {
                const b = binders.find(x => x.id === binderId);
                if (!b) return;
                toggleBinderPublicAndMirror(
                  b,
                  uid,
                  binderSlotMapRef.current.get(b.id) || new Set()
                );
              }}
            />
          ) : null}
        </div>
      </div>

      {hideBinderCards && (assignedIds.size > 0 || Array.from(binderSlotMapRef.current.values()).some(s => s.size > 0)) ? (
        <div className="text-xs text-slate-400">
          {Array.from(binderSlotMapRef.current.values()).reduce((n, s) => n + s.size, 0)} card
          {Array.from(binderSlotMapRef.current.values()).reduce((n, s) => n + s.size, 0) === 1 ? "" : "s"} assigned to binders are hidden. Uncheck “Hide cards in binders” to show all.
        </div>
      ) : null}

      {/* Deck shelf (collapsible; default collapsed) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white">Decks</div>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 rounded border border-white/10 bg-black/40 text-sm text-gray-100"
              onClick={() => setShowDecks((s) => !s)}
              aria-expanded={showDecks}
            >
              {showDecks ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        {/* NEW: deck public + cover card controls */}
        
        <div data-tour="collection-decks">
          {uid && showDecks ? (
            <DeckShelf
              uid={uid}
              cards={deckShelfCards}
              onTogglePublic={(deckId, currentPublic) => {
                const d = decks.find(x => x.id === deckId);
                if (!d) return;
                toggleDeckPublicAndMirror(
                  d,
                  uid,
                  deckSlotMapRef.current.get(d.id) || new Set(),
                  new Map(deckShelfCards.map(c => [c.id, c]))
                );
              }}
              onSetCoverCard={(deckId) => {
                const d = decks.find(x => x.id === deckId);
                if (!d) return;
                setDeckCoverCard(d, uid);
                // mirror sync after cover change
                syncPublicDeckMirror(
                  uid!,
                    d,
                    deckSlotMapRef.current.get(d.id) || new Set(),
                    new Map(deckShelfCards.map(c => [c.id, c]))
                );
              }}
            />
          ) : null}
        </div>
        </div>
 {/* Filter / Sort Toolbar */}
      {tab === "collection" && (
        <div className="flex flex-col gap-3 rounded border border-white/10 bg-black/40 p-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name / set / number..."
                className="rounded bg-black/60 border border-white/10 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="rounded bg-black/60 border border-white/10 px-2 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All</option>
                <option value="sale">For Sale</option>
                <option value="trade">For Trade</option>
                <option value="keep">Keep</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Group By</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as any)}
                className="rounded bg-black/60 border border-white/10 px-2 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="none">None</option>
                <option value="type">Type</option>
                <option value="color">Color</option>
                <option value="set">Set</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="rounded bg-black/60 border border-white/10 px-2 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="added">Date Added</option>
                <option value="name">Name</option>
                <option value="price">Price</option>
                <option value="mana">Mana Value</option>
                <option value="type">Type</option>
                <option value="set">Set</option>
                <option value="color">Color</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Direction</label>
              <select
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value as any)}
                className="rounded bg-black/60 border border-white/10 px-2 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
            {groupBy !== "none" && (
              <label className="flex items-center gap-2 mt-6 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={sortWithinGroups}
                  onChange={(e) => setSortWithinGroups(e.target.checked)}
                  className="accent-indigo-500"
                />
                Sort inside groups
              </label>
            )}
            <div className="flex-1" />
            <div className="text-xs text-slate-400 mt-6">
              {filteredCount.toLocaleString()} shown / {totalCollectionCount.toLocaleString()} total
            </div>
          </div>
        </div>
      )}
      {/* ===== COLLECTION SECTION ===== */}
      {tab === "collection" && (
        <section
          onDragOver={allowDrop}
          onDrop={handleDropBackToCollection}
          className="space-y-3"
        >
          <FoilStyles />

          {/* Pagination / counts bar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm text-slate-300">
                Collection: {filteredCount.toLocaleString()} shown / {totalCollectionCount.toLocaleString()} total
                {hideBinderCards && hiddenAssignedCount > 0 && (
                  <> (hiding {hiddenAssignedCount.toLocaleString()} assigned)</>
                )}
              </div>
              <div className="flex flex-wrap sm:flex-row sm:items-center gap-3">
                <label className="flex items-center gap-1 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    className="accent-indigo-500"
                    checked={hideBinderCards}
                    onChange={(e) => setHideBinderCards(e.target.checked)}
                  />
                  Hide cards in binders/decks
                </label>
                {!hideBinderCards && hiddenAssignedCount > 0 && (
                  <button
                    className="text-xs px-2 py-1 rounded bg-black/40 border border-white/10 hover:bg-black/50 text-slate-200"
                    onClick={() => setHideBinderCards(true)}
                  >
                    Hide assigned ({hiddenAssignedCount})
                  </button>
                )}
                {pendingCards && (
                  <span className="text-xs text-slate-400">Loading…</span>
                )}
              </div>
            </div>


          {groupBy === "none" ? (
            <UngroupedGrid
              items={pagedDisplayCards}
              fullLength={displayCards.length}
              pageSize={COLLECTION_PAGE_SIZE}
              hasMore={!allCards && hasMoreCards}
              loading={pendingCards || allCardsLoading}
              onLoadMore={!allCards ? () => loadCards(false) : undefined}
              openOracle={openOracle}
              openMoveDialog={openMoveDialog}
              openEditor={openEditor}
              onDeleteCard={onDeleteCard}
              uid={uid}
              openMoveToDeck={openMoveToDeck}
            />
          ) : (
            <GroupedGrid
              grouped={grouped!}
              orderKeys={orderGroupKeys}
              paged3x3={false}
              page={0}
              openOracle={openOracle}
              openMoveDialog={openMoveDialog}
              openEditor={openEditor}
              onDeleteCard={onDeleteCard}
              uid={uid}
              openMoveToDeck={openMoveToDeck}
            />
          )}
        </section>
      )}

  {/* ===== WISHLIST SECTION (restored) ===== */}
      {tab === "wishlist" && (
        <section data-tour="collection-wishlist" className="space-y-3">
          <h2 className="text-lg font-semibold text-white">Wishlist</h2>

          {/* Optional quick-search term applies via wishSearch (kept state) */}
          {(!wishes.length ||
            wishes.filter((w) =>
              (w.name || "")
                .toLowerCase()
                .includes(wishSearch.trim().toLowerCase())
            ).length === 0) &&
          !pendingWishes ? (
            <div className="text-sm text-slate-400">No wishes found.</div>
          ) : null}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {wishes
              .filter((w) =>
                (w.name || "")
                  .toLowerCase()
                  .includes(wishSearch.trim().toLowerCase())
              )
              .map((w) => (
                <article
                  key={w.id}
                  className="rounded border border-gray-700 bg-black/30 overflow-hidden"
                >
                  <div
                    className="relative aspect-[63/88] bg-black cursor-zoom-in"
                    onClick={() => openOracle("wishlist", w)}
                    role="button"
                    title={w.name}
                  >
                    <img
                      src={w.image || "/images/cardback.png"}
                      alt={w.name}
                      className="absolute inset-0 w-full h-full object-contain"
                      loading="lazy"
                    />
                    <div className="absolute top-1 right-1 flex flex-col gap-1">
                      <button
                        type="button"
                        className="p-1.5 rounded-md bg-black/65 hover:bg-black/75 text-yellow-300 shadow border border-white/10"
                        title="Edit wishlist item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWishlistEditItem(w);
                          setWishlistEditOpen(true);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                        >
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="p-1.5 rounded-md bg-black/65 hover:bg-black/75 text-red-400 shadow border border-white/10"
                        title="Remove from wishlist"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Remove this wish?")) onDeleteWish(w.id);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                        >
                          <path
                            d="M6 18L18 6M6 6l12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
          </div>

          {hasMoreWishes && (
            <div className="flex w-full justify-center mt-4">
              <Button
                variant="outline"
                onClick={() => loadWishes(false)}
                disabled={pendingWishes}
              >
                {pendingWishes ? "Loading..." : "Load more wishes"}
              </Button>
            </div>
          )}
        </section>
      )}

      {/* === Deck Card Picker Modal (entire collection) === */}
      <Dialog open={deckCardPickerOpen} onOpenChange={setDeckCardPickerOpen}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>Add Cards To Deck</DialogTitle>
            <DialogDescription>
              Browse your entire collection. Click a card to add it to the selected deck.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              value={deckPickerSearch}
              onChange={(e) => setDeckPickerSearch(e.target.value)}
              placeholder="Search full collection..."
              className="w-full rounded border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="text-xs text-slate-400">
              Showing {fullCollectionForPicker.length.toLocaleString()} cards
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 max-h-[55vh] overflow-y-auto pr-1">
              {fullCollectionForPicker.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    if (deckCardPickerTarget) addCardToDeck(deckCardPickerTarget, c.id);
                  }}
                  className="relative aspect-[63/88] rounded border border-white/10 bg-black/50 hover:border-indigo-400 overflow-hidden group"
                  title={`${c.name}`}
                >
                  <img
                    src={c.image || "/images/cardback.png"}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-contain"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70 text-[10px] text-slate-100 line-clamp-2">
                    {c.name}
                  </div>
                  <div className="absolute inset-0 hidden group-hover:flex items-center justify-center">
                    <span className="px-2 py-1 rounded bg-indigo-600/80 text-white text-xs">
                      Add
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeckCardPickerOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to binder dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[min(100vh-4rem,70vh)] overflow-y-auto p-0">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>Move to binder</DialogTitle>
            <DialogDescription>Select a binder to move this card into.</DialogDescription>
          </DialogHeader>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-2">
              {binders.map((b) => (
                <button
                  key={b.id}
                  onClick={() => handleSelectBinder(b.id)}
                  className="rounded-md border border-white/10 bg-black/50 hover:bg-black/60 px-3 py-2 text-left text-slate-200"
                >
                  {b.name}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="px-4 py-3 border-t border-white/10">
            <Button variant="outline" onClick={() => setMoveOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to deck dialog */}
      <Dialog open={moveDeckOpen} onOpenChange={setMoveDeckOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[min(100vh-4rem,70vh)] overflow-y-auto p-0">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>Add to deck</DialogTitle>
            <DialogDescription>Select a deck to add this card into.</DialogDescription>
          </DialogHeader>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-2">
              {decks.map((d) => (
                <button
                  key={d.id}
                  onClick={() => handleSelectDeck(d.id)}
                  className="rounded-md border border-white/10 bg-black/50 hover:bg-black/60 px-3 py-2 text-left text-slate-200"
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="px-4 py-3 border-t border-white/10">
            <Button variant="outline" onClick={() => setMoveDeckOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Importer modal */}
      <ImportDeckModal
        open={importOpen}
        onOpenChange={setImportOpen}
        dest={importDest}
        onDestChange={setImportDest}
      />

      {/* Scanner/OCR modal */}
      <ScanOCRModal
        open={scanOpen}
        onOpenChange={setScanOpen}
        onResult={async (result) => {
          setScanOpen(false);
          await addResolvedCard(result);
        }}
        onError={(msg) => setErrorMsg(msg)}
      />

      {/* Add modal */}
      <AddToCollectionModal
        open={addOpen}
        card={addCard}
        onClose={() => setAddOpen(false)}
        onAdded={(newDoc) => {
          if (newDoc?.id) {
            setCards((prev) => {
               const map = new Map(prev.map((c) => [c.id, c]));
               map.set(newDoc.id, {
                 ...newDoc,
                 createdAt: (newDoc as any).createdAt ?? serverTimestamp(),
               } as UserCardDoc);
               const arr = Array.from(map.values());
               arr.sort((a, b) => compareCards(a, b, sortBy, sortDir));
               return arr;
             });
          }
          setCards([]);
          lastCardRef.current = null;
          void loadCards(true);
        }}
      />

      {/* Edit modal */}
      {editExisting ? (
        <EditCollectionModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          card={editCard}
          existing={editExisting}
          onUpdated={() => {
            setEditOpen(false);
            setCards([]);
            lastCardRef.current = null;
            void loadCards(true);
          }}
        />
      ) : null}

      {/* Wishlist variant modal */}
      <WishlistVariantModal
        open={wishlistEditOpen}
        onOpenChange={(o) => {
          setWishlistEditOpen(o);
          if (!o) setWishlistEditItem(null);
        }}
        item={wishlistEditItem}
        uid={uid}
        onUpdated={(updated) => {
          if (updated) {
            setWishes((prev) =>
               prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)),
            );
          }
        }}
      />

      {/* Oracle modal */}
      <Dialog open={oracleOpen} onOpenChange={(o) => { if (!o) closeOracle(); }}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>{oracleCard?.name || "Card Details"}</DialogTitle>
            <DialogDescription>
              {oracleBusy
                ? "Loading oracle text..."
                : oracleCard?.oracle_text
                ? "Oracle text"
                : "No oracle text available."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {oracleCard?.image && (
              <img
                src={oracleCard.image}
                alt={oracleCard.name}
                className="w-full rounded border border-white/10"
                loading="lazy"
              />
            )}
            {oracleCard?.oracle_text && (
              <pre className="whitespace-pre-wrap text-sm text-slate-200 bg-black/30 p-3 rounded border border-white/10 max-h-72 overflow-y-auto">
                {oracleCard.oracle_text}
              </pre>
            )}
            <div className="text-xs text-slate-400 flex flex-wrap gap-3">
              {oracleCard?.set && <span>Set: {oracleCard.set.toUpperCase()}</span>}
              {oracleCard?.number && <span>No: {oracleCard.number}</span>}
              {oracleFrom && <span>Source: {oracleFrom}</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeOracle}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

/* ========= Subcomponents ========= */

function Pager({
  page,
  totalItems,
  pageSize,
  onFirst,
  onPrev,
  onNext,
  onLast,
}: {
  page: number;
  totalItems: number;
  pageSize: number;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
}) {
  const total = Math.max(1, Math.ceil(totalItems / pageSize));
  const atStart = page <= 0;
  const atEnd = page >= total - 1;
  return (
    <div className="inline-flex items-center gap-1">
      <Button variant="outline" size="sm" onClick={onFirst} disabled={atStart} title="First page">
        «
      </Button>
      <Button variant="outline" size="sm" onClick={onPrev} disabled={atStart} title="Previous page">
        ‹
      </Button>
      <span className="px-2 text-sm text-slate-300">
        Page <span className="font-semibold">{page + 1}</span> / {total}
      </span>
      <Button variant="outline" size="sm" onClick={onNext} disabled={atEnd} title="Next page">
        ›
      </Button>
      <Button variant="outline" size="sm" onClick={onLast} disabled={atEnd} title="Last page">
        »
      </Button>
    </div>
  );
}

function CollectionPager({
  groupBy,
  grouped,
  ungrouped,
  colPage,
  setColPage,
}: {
  groupBy: "none" | "type" | "color" | "set";
  grouped: Map<string, UserCardDoc[]> | null;
  ungrouped: UserCardDoc[];
  colPage: number;
  setColPage: (n: number) => void;
}) {
  const totalItems =
    groupBy === "none"
      ? ungrouped.length
      : Array.from(grouped?.values() ?? []).reduce((acc, arr) => acc + arr.length, 0);
  const totalPages = Math.max(1, Math.ceil(totalItems / LOCAL_PAGE_SIZE_3x3));
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold text-white">Collection</h2>
      <Pager
        page={colPage}
        totalItems={totalItems}
        pageSize={LOCAL_PAGE_SIZE_3x3}
        onFirst={() => setColPage(0)}
        onPrev={() => setColPage((p) => Math.max(0, p - 1))}
        onNext={() => setColPage((p) => Math.min(totalPages - 1, p + 1))}
        onLast={() => setColPage(totalPages - 1)}
      />
    </div>
  );
}

function UngroupedGrid({
  items,
  fullLength,
  pageSize,
  hasMore,
  loading,
  onLoadMore,
  openOracle,
  openMoveDialog,
  openEditor,
  onDeleteCard,
  uid,
  openMoveToDeck,
}: {
  items: UserCardDoc[];
  fullLength: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
  onLoadMore?: () => void;
  openOracle: (from: "collection", c: UserCardDoc) => void;
  openMoveDialog: (id: string) => void;
  openEditor: (c: UserCardDoc) => void;
  onDeleteCard: (c: UserCardDoc) => void;
  uid: string | null;
  openMoveToDeck: (id: string) => void;
}) {
  const gridCols = "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6";
  return (
    <>
      {fullLength === 0 && !loading ? (
        <div className="text-sm text-slate-400">No cards found.</div>
      ) : null}
      <div className={`grid ${gridCols} gap-3`}>
        {items.map((c) => (
          <CardTile
            key={c.id}
            c={c}
            uid={uid}
            context="collection"
            actionsSide="left"
            onMoveToBinder={(id) => openMoveDialog(id)}
            onAddToDeck={(id) => openMoveToDeck(id)}
            onEdit={(card) => openEditor(card as any)}
            onDelete={(card) => {
              if (confirm("Remove this card from your collection?")) {
                onDeleteCard(card as any);
              }
            }}
            onOpenOracle={(card) => openOracle("collection", card as any)}
            showOracleOnHover={false}
          />
        ))}
      </div>
      {hasMore && onLoadMore && (
        <div className="flex w-full justify-center mt-4">
          <Button variant="outline" onClick={onLoadMore} disabled={loading}>
            {loading ? "Loading..." : "Load more cards"}
          </Button>
        </div>
      )}
    </>
  );
}

function GroupedGrid({
  grouped,
  orderKeys,
  paged3x3,
  page,
  openOracle,
  openMoveDialog,
  openEditor,
  onDeleteCard,
  uid,
  openMoveToDeck,
}: {
  grouped: Map<string, UserCardDoc>;
  orderKeys: (keys: string[]) => string[];
  paged3x3: boolean;
  page: number;
  openOracle: (from: "collection", c: UserCardDoc) => void;
  openMoveDialog: (id: string) => void;
  openEditor: (c: UserCardDoc) => void;
  onDeleteCard: (c: UserCardDoc) => void;
  uid: string | null;
  openMoveToDeck: (id: string) => void;
}) {
  const keys = useMemo(() => orderKeys(Array.from(grouped.keys())), [grouped, orderKeys]);

  const gridCols = paged3x3
    ? "grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3"
    : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6";

  if (!paged3x3) {
    return (
      <>
        {keys.map((key) => {
          const items = grouped.get(key) || [];
          if (items.length === 0) return null;
          return (
            <div key={key} className="mb-4">
              <div className="text-lg font-semibold text-white mb-2">{key}</div>
              <div className={`grid ${gridCols} gap-3`}>
                {items.map((c) => (
                  <CardTile
                    key={c.id}
                    c={c}
                    uid={uid}
                    context="collection"
                    actionsSide="left"
                    onMoveToBinder={(id) => openMoveDialog(id)}
                    onAddToDeck={(id) => openMoveToDeck(id)}
                    onEdit={(card) => openEditor(card as any)}
                    onDelete={(card) => {
                      if (confirm("Remove this card from your collection?")) {
                        onDeleteCard(card as any);
                      }
                    }}
                    onOpenOracle={(card) => openOracle("collection", card as any)}
                    showOracleOnHover={false}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  // Paged 3×3 over whole grouped list (headers shown for visible slice)
  const flattened: Array<{ header: string | null; c?: UserCardDoc }> = [];
  for (const k of keys) {
    flattened.push({ header: k });
    for (const c of grouped.get(k) || []) flattened.push({ header: null, c });
  }
  const justCards = flattened.filter((x) => x.c) as Array<{ header: null; c: UserCardDoc }>;
  const start = page * LOCAL_PAGE_SIZE_3x3;
  const sliceCards = justCards.slice(start, start + LOCAL_PAGE_SIZE_3x3);
  const visibleIds = new Set(sliceCards.map((x) => x.c.id));

  const visibleWithHeaders: Array<{ type: "header" | "card"; key: string; header?: string; c?: UserCardDoc }> = [];
  let currentHeader: string | null = null;
  for (const item of flattened) {
    if (item.header !== null) {
      currentHeader = item.header!;
    } else if (item.c && visibleIds.has(item.c.id)) {
      if (currentHeader) {
        visibleWithHeaders.push({ type: "header", key: `h-${currentHeader}`, header: currentHeader });
        currentHeader = null;
      }
      visibleWithHeaders.push({ type: "card", key: item.c.id, c: item.c });
    }
  }

  return (
    <>
      {visibleWithHeaders.map((row) =>
        row.type === "header" ? (
          <div key={row.key} className="text-lg font-semibold text-white mt-4 mb-2">{row.header}</div>
        ) : null
      )}
      <div className={`grid ${gridCols} gap-3`}>
        {visibleWithHeaders
          .filter((x) => x.type === "card")
          .map(({ c }) => (
            <CardTile
              key={c!.id}
              c={c!}
              uid={uid}
              context="collection"
              actionsSide="left"
              onMoveToBinder={(id) => openMoveDialog(id)}
              onAddToDeck={(id) => openMoveToDeck(id)}
              onEdit={(card) => openEditor(card as any)}
              onDelete={(card) => {
                if (confirm("Remove this card from your collection?")) {
                  onDeleteCard(card as any);
                }
              }}
              onOpenOracle={(card) => openOracle("collection", card as any)}
              showOracleOnHover={false}
            />
          ))}
      </div>
    </>
  );
}

// NOTE: addResolvedCard is referenced by ScanOCRModal.onResult
async function addResolvedCard(_result: any) {
  // keep as a no-op here or implement your specific add flow
  return;
}

// === ADDED: public mirror sync helpers for binders & decks ===
async function syncPublicBinderMirror(uid: string, binder: BinderLite, slots: Set<string>) {
  try {
    const root = fsCollection(db, "users", uid, "public_binders");
    if (binder.public) {
      await setDoc(doc(root, binder.id), {
        name: binder.name,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      // Mirror slot assignments (full set)
      const assignCol = fsCollection(db, "users", uid, "public_binder_assignments");
      // Clear prior assignments for this binder (best‑effort)
      const prev = await getDocs(query(assignCol, where("binderId", "==", binder.id)));
      const delOps: Promise<any>[] = [];
      prev.forEach(d => delOps.push(deleteDoc(d.ref)));
      await Promise.allSettled(delOps);
      const batchSize = 400;
      const all = Array.from(slots);
      for (let i = 0; i < all.length; i += batchSize) {
        const slice = all.slice(i, i + batchSize);
        const writes = slice.map(cardId =>
          setDoc(
            doc(assignCol),
            {
              binderId: binder.id,
              userCardId: cardId,
              // no extra indices; reader side only needs mapping
              createdAt: serverTimestamp(),
            },
            { merge: false },
          ),
        );
        await Promise.allSettled(writes);
      }
    } else {
      // Remove binder + its slot mirrors
      await Promise.allSettled([
        deleteDoc(doc(root, binder.id)),
      ]);
      const assignCol = fsCollection(db, "users", uid, "public_binder_assignments");
      const prev = await getDocs(query(assignCol, where("binderId", "==", binder.id)));
      const delOps: Promise<any>[] = [];
      prev.forEach(d => delOps.push(deleteDoc(d.ref)));
      await Promise.allSettled(delOps);
    }
  } catch (e) {
    console.warn("[MyCollection] syncPublicBinderMirror failed", e);
  }
}

async function syncPublicDeckMirror(uid: string, deck: DeckLite, slots: Set<string>, deckCardsIndex: Map<string, UserCardDoc>) {
  try {
    const root = fsCollection(db, "users", uid, "public_decks");
    if (deck.public) {
      // Commander & colors detection
      const commanderId = deck.commanderId || null;
      const commanderCard = commanderId ? deckCardsIndex.get(commanderId) || null : null;
      const colors = Array.isArray(deck.colorIdentity) ? deck.colorIdentity : (commanderCard?.color_identity || commanderCard?.colors || null);
      // Cover: priority commander > explicit coverCardId > first slot
      let coverCard: UserCardDoc | null = null;
      if (commanderCard) coverCard = commanderCard;
      else if (deck.coverCardId) coverCard = deckCardsIndex.get(deck.coverCardId) || null;
      else {
        const first = Array.from(slots)[0];
        if (first) coverCard = deckCardsIndex.get(first) || null;
      }
      const coverImage = coverCard?.custom_image_url || coverCard?.image || null;
      const slug = deck.slug || deckSlug(deck.name);

      await setDoc(doc(root, deck.id), {
        name: deck.name,
        slug: slug,
        color_identity: colors || [],
        commanderId,
        coverCardId: deck.coverCardId || (coverCard?.id ?? null),
        coverImage: coverImage ?? null,
        format: deck.format || null,
        updatedAt: serverTimestamp(),
        // lightweight commander snapshot (for highlight if card becomes private)
        commanderSnapshot: commanderCard
          ? {
              name: commanderCard.name,
              set: commanderCard.set,
              number: commanderCard.number,
              image: commanderCard.image,
              foil: commanderCard.foil,
              colors: commanderCard.color_identity || commanderCard.colors || null,
            }
          : null,
      }, { merge: true });

      // Mirror slots
      const slotCol = fsCollection(db, "users", uid, "public_deck_slots");
      const prev = await getDocs(query(slotCol, where("deckId", "==", deck.id)));
      const delOps: Promise<any>[] = [];
      prev.forEach(d => delOps.push(deleteDoc(d.ref)));
      await Promise.allSettled(delOps);
      const all = Array.from(slots);
      const batchSize = 400;
      for (let i = 0; i < all.length; i += batchSize) {
        const slice = all.slice(i, i + batchSize);
        await Promise.allSettled(slice.map(cardId =>
          setDoc(doc(slotCol), {
            deckId: deck.id,
            userCardId: cardId,
            createdAt: serverTimestamp(),
          }),
        ));
      }
    } else {
      await Promise.allSettled([
        deleteDoc(doc(root, deck.id)),
      ]);
      const slotCol = fsCollection(db, "users", uid, "public_deck_slots");
      const prev = await getDocs(query(slotCol, where("deckId", "==", deck.id)));
      const delOps: Promise<any>[] = [];
      prev.forEach(d => delOps.push(deleteDoc(d.ref)));
      await Promise.allSettled(delOps);
    }
  } catch (e) {
    console.warn("[MyCollection] syncPublicDeckMirror failed", e);
  }
}

// Wrap existing toggle helpers so mirrors update instantly
async function toggleBinderPublicAndMirror(binder: BinderLite, uid?: string | null, slots?: Set<string>) {
  await toggleBinderPublic(binder, uid);
  if (uid) {
    const next = { ...binder, public: !binder.public };
    await syncPublicBinderMirror(uid, next, slots || new Set());
  }
}
async function toggleDeckPublicAndMirror(deck: DeckLite, uid?: string | null, slots?: Set<string>, deckCardsIndex?: Map<string, UserCardDoc>) {
  await toggleDeckPublic(deck, uid);
  if (uid) {
    const next = { ...deck, public: !deck.public };
    await syncPublicDeckMirror(uid, next, slots || new Set(), deckCardsIndex || new Map());
  }
}

